import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Key,
  ClipboardCheck,
  Home,
  Sparkles,
  DollarSign,
  ArrowRight,
  Star
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const MoveInOutCleaningPage = () => {
  const services = [
    {
      type: "Move-Out Cleaning",
      icon: Key,
      description: "Get your security deposit back and leave on good terms",
      benefits: [
        "Inspection-ready results",
        "Every surface cleaned and sanitized",
        "Inside appliances, cabinets, and closets",
        "Walls, baseboards, and fixtures detailed",
        "Perfect for renters and homeowners"
      ]
    },
    {
      type: "Move-In Cleaning",
      icon: Home,
      description: "Start fresh in a spotless space before unpacking",
      benefits: [
        "Deep clean before you move in",
        "Sanitize kitchens and bathrooms",
        "Remove previous owner residue",
        "Peace of mind for your family",
        "Ready for your first day"
      ]
    }
  ];

  const whoWeServe = [
    {
      title: "Renters",
      description: "Leaving an apartment or rental home? Our move-out cleaning helps ensure you meet lease requirements and maximize your chances of getting your full security deposit back."
    },
    {
      title: "Homeowners",
      description: "Selling your home? A professional move-out clean makes your property show-ready and leaves a positive final impression for the new owners."
    },
    {
      title: "Landlords & Property Managers",
      description: "Get units turnover-ready fast. We work with property managers throughout Brevard County to prepare vacant units for new tenants on tight timelines."
    },
    {
      title: "Realtors",
      description: "Listing preparation matters. A spotless home photographs better, shows better, and can even help justify your asking price. We support Brevard County Realtors with pre-listing and closing cleans."
    }
  ];

  const faqs = [
    {
      question: "What's included in a move-out cleaning?",
      answer: "Everything. We clean inside appliances (oven, fridge, microwave), all cabinets and drawers, baseboards, walls, windows, bathrooms top to bottom, floors, light fixtures, and more. It's a comprehensive clean designed to meet strict inspection standards."
    },
    {
      question: "How long does a move-in or move-out clean take?",
      answer: "Most homes take 4-8 hours depending on size and condition. We never rush. Heather's 15+ years of experience means we know what landlords and property managers look for, and we make sure it's done right."
    },
    {
      question: "Can you guarantee I'll get my deposit back?",
      answer: "We can't guarantee deposit returns since that's ultimately up to your landlord or property manager. However, our thorough cleaning gives you the best possible chance. One recent client received their full $2,000 security deposit after our move-out service—and they were thrilled."
    },
    {
      question: "Do I need to be present during the cleaning?",
      answer: "No, most clients drop off keys or arrange access and pick them up when we're done. We're fully insured, and Bob and Heather personally oversee every job."
    },
    {
      question: "Can you clean a home that's already empty?",
      answer: "Absolutely. In fact, it's easier! Empty homes allow us to access every corner, baseboard, and surface without working around furniture."
    }
  ];

  // Structured Data
  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Move-In & Move-Out Cleaning | Pierce Premier Cleaning",
      description: "Professional move-in and move-out cleaning in Brevard County. Inspection-ready results for renters, homeowners, landlords, and Realtors. Locally owned, fully insured.",
      url: "/services/move-in-move-out-cleaning"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Move-In / Move-Out Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Move-In and Move-Out Cleaning Service",
      description: "Comprehensive move-in and move-out cleaning services for renters, homeowners, landlords, and real estate professionals throughout Brevard County."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="Move-In & Move-Out Cleaning | Pierce Premier | Brevard County, FL"
        description="Move-in & move-out cleaning in Brevard County. Inspection-ready results. Help maximize your security deposit return. Serving renters, landlords, and Realtors. Call 321-408-1347."
        canonical="/services/move-in-move-out-cleaning"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-50 via-pink-50 to-cyan-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-purple-600 text-white mb-4">Moving Made Easy</Badge>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Move-In & Move-Out Cleaning
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Moving is stressful enough. Let us handle the cleaning. Whether you&apos;re leaving a rental and need your deposit back or moving into a new home and want it spotless before unpacking, Pierce Premier delivers inspection-ready results.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  With Heather&apos;s 15+ years of executive housekeeping experience, we know exactly what landlords, property managers, and new homeowners expect—and we exceed it.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional move-in and move-out cleaning in Brevard County"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Move-In vs Move-Out */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Move-Out or Move-In? We&apos;ve Got You Covered.
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Both services are thorough, but tailored to your specific needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => {
                const Icon = service.icon;
                return (
                  <Card key={idx} className="border-2 hover:border-purple-200 transition-colors">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl flex items-center justify-center mb-4">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.type}</h3>
                      <p className="text-gray-600 mb-6">{service.description}</p>
                      <ul className="space-y-3">
                        {service.benefits.map((benefit, benefitIdx) => (
                          <li key={benefitIdx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Success Story */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-purple-200 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Real Customer Success</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      A recent client was moving out of their rental home in Melbourne and needed a thorough move-out clean to meet their lease requirements. They had a $2,000 security deposit on the line and were understandably stressed about the condition expectations.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      After Pierce Premier&apos;s detailed move-out cleaning—inside every appliance, all cabinets, baseboards, walls, bathrooms, and floors—the property passed inspection with flying colors.
                    </p>
                    <p className="text-gray-700 leading-relaxed font-semibold">
                      Result? They received their full $2,000 deposit back and left a glowing review.
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-white rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Important:</span> While we can&apos;t guarantee deposit returns (that&apos;s ultimately up to your landlord), our inspection-ready cleaning gives you the best possible outcome. Stories like this are why Brevard County families trust Pierce Premier.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Who We Serve */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Who We Serve
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Move-in and move-out cleaning for everyone involved in the moving process.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whoWeServe.map((audience, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{audience.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{audience.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                What&apos;s Included?
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                A complete, inspection-ready clean from top to bottom.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                "Kitchen: Inside oven, fridge, microwave, dishwasher, all cabinets and drawers",
                "Bathrooms: Scrubbed tubs, showers, toilets, sinks, mirrors, all surfaces",
                "All rooms: Baseboards, walls spot-cleaned, light fixtures, ceiling fans",
                "Floors: Vacuumed, mopped, all corners and edges",
                "Windows: Interior window cleaning, tracks, and sills",
                "Closets: Shelves wiped, floors cleaned",
                "Doors & trim: All door frames, handles, and trim wiped down",
                "Fixtures: Light switches, outlet covers, vents cleaned",
                "Final walkthrough: Detailed inspection to ensure nothing is missed"
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">
                Need something specific added? We&apos;re flexible and can customize the service to meet your landlord&apos;s or property manager&apos;s requirements.
              </p>
              <Link to="/contact" className="text-purple-600 font-semibold hover:text-purple-700">
                Request Custom Quote →
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions About Move-In & Move-Out Cleaning
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-purple-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;Pierce Premier did an amazing job with our move-out cleaning. The house looked brand new! Very professional and thorough.&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">Sarah Johnson</p>
                <p className="text-gray-500 text-sm">Palm Bay, FL</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Serving All of Brevard County
            </h2>
            <p className="text-gray-600 mb-8">
              From apartments in Melbourne to beach rentals in Satellite Beach, we help Brevard County residents with stress-free moving transitions.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Palm Bay", "Melbourne", "Satellite Beach", "Indialantic", 
                "Indian Harbour Beach", "Cocoa Beach", "Merritt Island", 
                "Viera", "Rockledge", "West Melbourne", "Cocoa"
              ].map((city, idx) => (
                <Badge key={idx} variant="outline" className="text-sm py-2 px-4">
                  {city}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready for a Stress-Free Move?"
          description="Let Pierce Premier handle the cleaning so you can focus on everything else. Inspection-ready results you can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default MoveInOutCleaningPage;
